export type UserType = 'internal' | 'external';

const DEFAULT_USER_TYPE: UserType = 'internal';
export const SERVICE_ACCOUNT_BLOCKED = 'SERVICE_ACCOUNT_BLOCKED';
const listeners = new Set<() => void>();

let currentUserType: UserType | null = null;

function notify(): void {
  for (const listener of listeners) listener();
}

export function legacyUtToUserType(value: unknown): UserType | null {
  if (value === null || value === undefined || value === '') return null;
  return Number(value) === 1 ? 'internal' : 'external';
}

export function userTypeToLegacyUt(userType: UserType): 1 | 2 {
  return userType === 'internal' ? 1 : 2;
}

export function normalizeUserType(value: unknown): UserType | null {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'internal' || normalized === 'ey') return 'internal';
    if (normalized === 'external' || normalized === 'client') return 'external';
    return legacyUtToUserType(normalized);
  }

  if (typeof value === 'number') return legacyUtToUserType(value);

  if (typeof value === 'boolean') return value ? 'internal' : 'external';

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return (
      normalizeUserType(record.userType) ??
      normalizeUserType(record.ut) ??
      normalizeUserType(record.type) ??
      normalizeUserType(record.internalAD) ??
      normalizeUserType(record.isInternal)
    );
  }

  return null;
}

export function getCurrentUserType(): UserType {
  return currentUserType ?? DEFAULT_USER_TYPE;
}

export function getResolvedUserType(): UserType | null {
  return currentUserType;
}

export function setCurrentUserType(userType: UserType): void {
  if (currentUserType === userType) return;
  currentUserType = userType;
  // Persist the TENANT (not the user type) so legacy MFEs that read
  // 'ey.login.tnnt' to select an API scope resolve the INTERNAL resource. The
  // React shell is single-tenant / internal-only: only the internal app
  // registration + authority exist, so the internal tenant is the only one that
  // can mint tokens. Writing the user type here put 'external' in this key,
  // which made MFEs (e.g. orchestrator DocumentHandler) request an
  // external-tenant resource the internal tenant has no service principal for
  // -> AADSTS500011 invalid_resource. Mirrors Angular login.component.ts
  // setTenant('internal') under single-tenant auth. The user TYPE stays
  // available via getCurrentUserType() / EY_RAPToR_UserType().
  try {
    sessionStorage.setItem('ey.login.tnnt', 'internal');
    localStorage.setItem('ey.login.tnnt', 'internal');
  } catch {
    /* storage unavailable */
  }
  notify();
}

export function clearCurrentUserType(): void {
  if (currentUserType === null) return;
  currentUserType = null;
  try {
    sessionStorage.removeItem('ey.login.tnnt');
    localStorage.removeItem('ey.login.tnnt');
  } catch {
    /* storage unavailable */
  }
  notify();
}

export function setCurrentUserTypeFromProfile(profile: unknown): UserType | null {
  const userType = normalizeUserType(profile);
  if (!userType) return null;
  setCurrentUserType(userType);
  return userType;
}

export function isServiceAccountProfile(profile: unknown): boolean {
  if (!profile || typeof profile !== 'object') return false;
  return (profile as Record<string, unknown>).isServiceAccount === true;
}

export function createServiceAccountBlockedError(): Error {
  const err = new Error('Service account login blocked') as Error & { code?: string };
  err.code = SERVICE_ACCOUNT_BLOCKED;
  return err;
}

export function isServiceAccountBlockedError(error: unknown): boolean {
  return Boolean(
    error &&
    typeof error === 'object' &&
    (error as { code?: unknown }).code === SERVICE_ACCOUNT_BLOCKED,
  );
}

export function subscribeUserType(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
